import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { PartyOption } from "@/lib/parties/partiesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

type PartySelectProps = {
  value: string;
  options: PartyOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  triggerClassName?: string;
  /** When true, selecting the empty row clears the value (e.g. “All parties”). */
  clearable?: boolean;
  clearLabel?: string;
};

export function PartySelect({
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Select party",
  triggerClassName,
  clearable = false,
  clearLabel = "All parties",
}: PartySelectProps) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((opt) => String(opt.id) === String(value));
  const displayLabel = selectedOption
    ? selectedOption.name
    : placeholder;

  return (
    <Popover open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            "glass-input h-9 w-full justify-between font-normal text-xs px-3 min-w-0",
            !selectedOption && "text-muted-foreground",
            triggerClassName
          )}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 z-[100]" align="start" sideOffset={4}>
        <Command shouldFilter>
          <CommandInput placeholder="Search party…" className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              No party found.
            </CommandEmpty>
            <CommandGroup>
              {clearable ? (
                <CommandItem
                  value={`__clear__ ${clearLabel}`}
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  className="text-xs flex items-center justify-between cursor-pointer text-muted-foreground"
                >
                  <span className="truncate">{clearLabel}</span>
                  <Check
                    className={cn(
                      "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                      !value ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ) : null}
              {options.map((p) => {
                const optId = String(p.id);
                const isSelected = optId === String(value);
                const label = `${p.name}${p.phone ? ` · ${p.phone}` : ""}`;
                return (
                  <CommandItem
                    key={optId}
                    value={`${p.name} ${p.phone ?? ""}`}
                    onSelect={() => {
                      onChange(optId);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer"
                  >
                    <span className="truncate">{label}</span>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
